import { IAppliedFilter, IFeaturedOfferConfig, IFilterConfig } from "@/app/models/auctor"
import {
    evaluateExpressionWithFilters,
    evaluateExpressionWithFiltersAndRowData,
    getQueryParamsFromSearchParams,
} from "@/app/utils/expression-utils"

export const getFilterDisplayCondition = (
    filter: any,
    appliedFilters: IAppliedFilter[],
    queryParams?: Record<string, string> | null
) => {
    if (!filter.display_condition) {
        return true
    }

    try {
        return evaluateExpressionWithFilters(filter.display_condition, appliedFilters, queryParams)
    } catch (error) {
        console.error(`Error evaluating display condition for filter ${filter.variableName}:`, error)
        return true
    }
}

export const parseDefaultValueForFilterType = (filter: any, defaultPageValue?: any) => {
    if (filter.type === "select") {
        let defaultSelectValue = null
        if (!defaultPageValue) {
            defaultSelectValue = filter.options.find((option: any) => option.is_default)
        } else {
            defaultSelectValue = filter.options.find((option: any) => option.value === defaultPageValue)
        }

        if (!filter.is_multiple) {
            return defaultSelectValue
        }

        return defaultSelectValue ? [defaultSelectValue] : null
    }

    return defaultPageValue ?? filter.default_value
}

export const toNumber = (value: any) => {
    if (value !== undefined && value !== null && typeof value !== "number") {
        return parseFloat(value.toString().replace(/\s/g, ""))
    }

    return value
}

export const getFilterDefaultValueForPage = (filter: any, pageConfig?: any) => {
    const currentPageConfig = filter.page_configs.find((config: any) => config?.page?.id === pageConfig?.auctorPageId)

    return parseDefaultValueForFilterType(filter, currentPageConfig?.default_value)
}

export const valueMatchFilterConstraint = (value: any, filterValue: any, constraint: any) => {
    const numberConstraints = ["greater_than", "lower_than"]

    // preparse number values before applying constraints
    if (numberConstraints.includes(constraint)) {
        value = toNumber(value)
        filterValue = toNumber(filterValue)
    }

    switch (constraint) {
        case "equals":
            return value == filterValue
        case "not_equals":
            return value != filterValue
        case "greater_than":
            return value > filterValue
        case "lower_than":
            return value < filterValue
        case "contains":
            return value.includes(filterValue)
        case "not_contains":
            return !value.includes(filterValue)
        default:
            return true
    }
}

export const rowMatchFilterConstraint = (row: any, appliedFilter: IAppliedFilter) => {
    if (appliedFilter.value === null || appliedFilter.value === undefined) {
        return true
    }

    const valueToCheck = row[appliedFilter.targetColumn]
    let filterValue = appliedFilter.value

    if (appliedFilter.type !== "select") {
        return valueMatchFilterConstraint(valueToCheck, filterValue, appliedFilter.filterConstraint)
    }

    if (!appliedFilter.isMultiple) {
        // @ts-ignore because we know it's an object if we're in a select single, and we know it has a value property
        filterValue = appliedFilter.value?.value
        return valueMatchFilterConstraint(valueToCheck, filterValue, appliedFilter.filterConstraint)
    }

    if (appliedFilter.isStrictMultiple) {
        if (appliedFilter.value.length === 0) {
            return true
        }

        // @ts-ignore because we know it's an array if we're in a select multiple
        return appliedFilter.value.every((selectedValue: any) =>
            valueMatchFilterConstraint(valueToCheck, selectedValue.value, appliedFilter.filterConstraint)
        )
    }

    if (!appliedFilter.isStrictMultiple) {
        if (appliedFilter.value.length === 0) {
            return true
        }

        // @ts-ignore because we know it's an array if we're in a select multiple
        return appliedFilter.value.some((selectedValue: any) =>
            valueMatchFilterConstraint(valueToCheck, selectedValue.value, appliedFilter.filterConstraint)
        )
    }

    return true
}

export const evaluateDynamicDefaultValue = (
    filter: IFilterConfig,
    appliedFilters: IAppliedFilter[],
    queryParams?: Record<string, string> | null
): any => {
    if (!filter.dynamic_value_expression) {
        return null
    }

    try {
        const dynamicValue = evaluateExpressionWithFilters(filter.dynamic_value_expression, appliedFilters, queryParams)

        // For select filters, we need to find the matching option
        if (filter.type === "select" && dynamicValue !== null && dynamicValue !== undefined) {
            const options = (filter as any).options || []

            if (!(filter as any).is_multiple) {
                // Single select: find the option that matches the dynamic value
                const matchingOption = options.find(
                    (option: any) => option.value === dynamicValue || option.label === dynamicValue
                )
                return matchingOption || null
            } else {
                // Multiple select: handle arrays or single values
                const dynamicValues = Array.isArray(dynamicValue) ? dynamicValue : [dynamicValue]
                const matchingOptions = dynamicValues
                    .map((val: any) => options.find((option: any) => option.value === val || option.label === val))
                    .filter(Boolean) // Remove null/undefined values

                return matchingOptions.length > 0 ? matchingOptions : null
            }
        }

        return dynamicValue
    } catch (error) {
        console.warn(`Error evaluating dynamic value for filter ${filter.unique_id}:`, error)
        return null
    }
}

export const initAppliedFilters = (
    filtersConfig: IFilterConfig[],
    pageConfig?: any,
    queryParams?: Record<string, string> | null
) => {
    if (!filtersConfig) {
        return []
    }

    // Pass 1: Build all filters with their static default values
    const appliedFilters: IAppliedFilter[] = filtersConfig.map((filter: any) => {
        const defaultValue = getFilterDefaultValueForPage(filter, pageConfig)

        return {
            filterId: filter.unique_id,
            targetColumn: filter.target_column,
            type: filter.type,
            filterConstraint: filter.filter_constraint,
            value: defaultValue,
            isMultiple: filter.is_multiple,
            isStrictMultiple: filter.is_strict_multiple,
            variableName: filter.variable_name,
            dynamicValueExpression: filter.dynamic_value_expression,
        }
    })

    // Pass 2: Evaluate dynamic expressions with all filters available as context
    appliedFilters.forEach((appliedFilter, index) => {
        const filter = filtersConfig[index] as any
        if (filter.dynamic_value_expression) {
            const dynamicValue = evaluateDynamicDefaultValue(
                filter,
                appliedFilters,
                queryParams
            )

            if (dynamicValue !== null && dynamicValue !== undefined) {
                appliedFilter.value = dynamicValue
            }
        }
    })

    return appliedFilters
}

export const applyFiltersOnResults = (
    results: any,
    filtersConfig: IFilterConfig[],
    appliedFilters: IAppliedFilter[],
    expressionFilter: string | null | undefined,
    queryParams?: Record<string, string> | null
) => {
    // Safely ensure results is an array
    const safeResults = Array.isArray(results) ? results : []

    return safeResults.filter((row: any) => {
        let available = true
        for (const filter of appliedFilters) {
            const filterConfig = filtersConfig.find((f: IFilterConfig) => f.unique_id === filter.filterId)

            if (filterConfig && filterConfig.display_condition) {
                const shouldDisplayFilter = getFilterDisplayCondition(filterConfig, appliedFilters, queryParams)
                if (shouldDisplayFilter === false) {
                    continue
                }
            }

            const rowMatch = rowMatchFilterConstraint(row, filter)

            if (!rowMatch) {
                available = false
                break
            }
        }

        // handle expression filter
        if (expressionFilter) {
            const isOfferMatchingExpression = evaluateExpressionWithFiltersAndRowData(
                expressionFilter,
                row,
                appliedFilters,
                undefined,
                queryParams
            )

            if (!isOfferMatchingExpression) {
                available = false
            }
        }

        return available
    })
}

export const sortBetweenFeaturedAndNormalResults = (
    results: any[],
    appliedFilters: IAppliedFilter[],
    featuredOffersConfig?: IFeaturedOfferConfig[],
    queryParams?: Record<string, string> | null
) => {
    // Safely ensure results is an array
    const safeResults = Array.isArray(results) ? [...results] : []
    const featuredResults: any[] = []

    if (featuredOffersConfig) {
        featuredOffersConfig.forEach((featuredOfferConfig: any) => {
            // check if the featured rule appliance condition is met
            const featuredRuleApplianceCondition = featuredOfferConfig.application_condition_expression
            const shouldFeaturedRuleApply = evaluateExpressionWithFilters(
                featuredRuleApplianceCondition,
                appliedFilters,
                queryParams
            )

            if (shouldFeaturedRuleApply) {
                // apply the featured offer selection expression
                safeResults.forEach((result: any) => {
                    const isOfferFeatured = evaluateExpressionWithFiltersAndRowData(
                        featuredOfferConfig.offer_selection_expression,
                        result,
                        appliedFilters,
                        undefined,
                        queryParams
                    )

                    if (isOfferFeatured) {
                        // remove the result from the results array
                        const index = safeResults.indexOf(result)
                        if (index > -1) {
                            safeResults.splice(index, 1)
                        }

                        featuredResults.push({
                            ...result,
                            isFeatured: true,
                        })
                    }
                })
            }
        })
    }

    return {
        featuredResults: featuredResults,
        results: safeResults,
    }
}

/**
 * Initialize applied filters with query parameters for server-side usage
 * This function is designed to be used in server components where you have access to searchParams
 *
 * @param filtersConfig - Array of filter configurations
 * @param pageConfig - Page configuration for default values
 * @param searchParams - Next.js searchParams object from server components
 * @returns Array of initialized applied filters with dynamic values from query parameters
 *
 * @example
 * // In a server component:
 * import { initAppliedFiltersWithSearchParams } from '@/app/utils/filters-utils';
 *
 * export default function ServerComparator({ searchParams, filtersConfig, pageConfig }) {
 *   const appliedFilters = initAppliedFiltersWithSearchParams(
 *     filtersConfig,
 *     pageConfig,
 *     searchParams
 *   );
 *
 *   // Now appliedFilters have dynamic values based on URL query parameters
 *   return <div>...</div>;
 * }
 */
export const initAppliedFiltersWithSearchParams = (
    filtersConfig: IFilterConfig[],
    pageConfig?: any,
    searchParams?: { [key: string]: string | string[] | undefined }
): IAppliedFilter[] => {
    // Convert Next.js searchParams to query parameters object
    const queryParams = searchParams ? getQueryParamsFromSearchParams(searchParams) : null

    return initAppliedFilters(filtersConfig, pageConfig, queryParams)
}
